import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { DeepReadonly } from "ts-essentials";

import { readState } from "@/__main__/app-state.mjs";
import { FactionRE } from "@/game-eft/constants/constants.mjs";
import type { RegisterPlayer } from "@/game-eft/models/game-events.mjs";
import type { LiveGame } from "@/game-eft/utils/actions.mjs";
import isBotAccount from "@/game-eft/utils/is-bot-account.mjs";
import isObject from "@/util/is-object.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

// Hooks
export function useAllRegisterPlayers() {
  const { t } = useTranslation();
  const state = useSnapshot(readState);
  const liveGame = state.eft.liveGame;
  const players = useMemo(() => getAllRegisterPlayers(liveGame, t), [liveGame]);
  return players;
}

export function useTeammates() {
  const { t } = useTranslation();
  const state = useSnapshot(readState);
  const liveGame = state.eft.liveGame;
  const teammates = useMemo(() => getTeammates(liveGame, t), [liveGame]);
  return teammates;
}

export function useLocalPlayer() {
  const state = useSnapshot(readState);
  const liveGame = state.eft.liveGame;
  const player = useMemo(() => getLocalRegisterPlayer(liveGame), [liveGame]);
  return player;
}

export function useLocalProfile() {
  const state = useSnapshot(readState);
  const liveGame = state.eft.liveGame;
  const profile = useMemo(() => getLocalProfile(liveGame), [liveGame]);
  return profile;
}

export function getLocalProfile(liveGame: DeepReadonly<LiveGame> | LiveGame) {
  return liveGame?.profileUpdate?.all_profiles.find((i) => i);
}

export function getLocalRegisterPlayer(
  liveGame: DeepReadonly<LiveGame> | LiveGame,
): RegisterPlayer | undefined {
  const anyProfile = getLocalProfile(liveGame);
  if (!anyProfile || !liveGame?.registerPlayer) return undefined;
  const allPlayersWithProfiles = getGroupedProfiles(liveGame);
  const player = allPlayersWithProfiles.find(
    (i) => i.accountId === anyProfile.account_id,
  );
  if (!player) return undefined;
  return Object.values(liveGame.registerPlayer).find(
    (i) => i.player.profile_id === player.characterId,
  );
}

export function getAllRegisterPlayers(
  liveGame: DeepReadonly<LiveGame> | LiveGame,
  t?: TFunction,
): {
  [profileId: string]: RegisterPlayer & { nickname?: string };
} {
  const fallbackNickname = t?.("common:player", "Player") || "Player";
  const players = Object.create(null);
  // Method 1: RegisterUser
  addRegisterPlayers(players, liveGame, fallbackNickname, false);
  // Method 2: PlayerShot
  addPlayerShot(players, liveGame, fallbackNickname, false);
  // Method 3: ItemAddedRemoved
  addItemAddedRemoved(players, liveGame, fallbackNickname, false);
  return players;
}

export function getTeammates(
  liveGame: DeepReadonly<LiveGame> | LiveGame,
  t: TFunction,
): {
  [profileId: string]: {
    accountId: string;
    profileId: string;
    nickname: string;
  };
} {
  const localProfile = getLocalProfile(liveGame);
  const localPlayer = getLocalRegisterPlayer(liveGame);
  const localAccountId =
    localProfile?.account_id || localPlayer?.player.account_id;
  const localGroupId =
    Object.values(liveGame?.registerPlayer ?? {})
      .filter((i) => i.player.account_id === localAccountId)
      .find((i) => i.player.group_id)?.player.group_id ||
    btoa(new Date().toISOString());
  const fallbackNickname = t("common:teammate", "Teammate");
  const teammates: {
    [profileId: string]: {
      accountId: string;
      profileId: string;
      nickname: string;
    };
  } = Object.create(null);
  // All of these methods are for simply finding out how to obtain the nickname of the user, lols
  // Method 1: RegisterUser
  addRegisterPlayers(teammates, liveGame, fallbackNickname, true, {
    groupId: localGroupId,
    accountId: localAccountId,
  });
  // Method 2: PlayerShot
  addPlayerShot(teammates, liveGame, fallbackNickname, true, {
    groupId: localGroupId,
  });
  // Method 3: ItemAddedRemoved
  addItemAddedRemoved(teammates, liveGame, fallbackNickname);
  // Remove any profile ids that should not be present in the teammates object
  const groupedProfiles = getGroupedProfiles(liveGame);
  for (const profileId in teammates) {
    const teammate = teammates[profileId];
    const groupedProfile = groupedProfiles.find(
      (i) => i.accountId === teammate.accountId,
    );
    const characterIdsAndFaction = Object.entries(groupedProfile.characterIds);
    // Priortize the PMC profile for the correct username, if thats not possible, we must use their Russian EFT name
    const pmcProfile = characterIdsAndFaction.find(([, faction]) =>
      [FactionRE.bear, FactionRE.usec].some((i) => i === faction),
    );
    const scavProfile = characterIdsAndFaction.find(
      ([, faction]) => FactionRE.savage === faction,
    );
    if (!pmcProfile && !scavProfile) continue;
    if (pmcProfile && scavProfile) delete teammates[scavProfile[0]];
  }
  return teammates;
}

// Utilities for this file only
function betterNickname(nickname: unknown, defaultNickname: string): string {
  if (typeof nickname === "string" && nickname && nickname !== defaultNickname)
    return nickname;
  return defaultNickname;
}

function addRegisterPlayers(
  source: object,
  liveGame: DeepReadonly<LiveGame> | LiveGame,
  fallbackNickname: string,
  init = false,
  filters?: { groupId: string; accountId: string },
): void {
  if (filters && !filters.accountId) return;
  const allRegisteredPlayers = liveGame?.registerPlayer;
  for (const profileId in allRegisteredPlayers) {
    const i = allRegisteredPlayers[profileId];
    if (
      filters?.groupId &&
      filters?.accountId &&
      i.player.group_id !== filters.groupId &&
      i.player.account_id !== filters.accountId
    )
      continue;
    const player = isObject(i.player.profile) ? i.player.profile : undefined;
    if (init && !source[i.player.profile_id])
      source[i.player.profile_id] = {
        accountId: i.player.account_id,
        profileId: i.player.profile_id,
      };
    if (source[i.player.profile_id])
      source[i.player.profile_id].nickname = betterNickname(
        player?.nickname,
        fallbackNickname,
      );
  }
}

function addPlayerShot(
  source: object,
  liveGame: DeepReadonly<LiveGame> | LiveGame,
  fallbackNickname: string,
  init = false,
  filters?: { groupId: string },
): void {
  const target = liveGame?.playersShot;
  if (!Array.isArray(target)) return;
  for (const playerShot of target) {
    if (
      !filters ||
      (filters?.groupId && filters.groupId === playerShot.player.group_id)
    ) {
      if (init && !source[playerShot.player.profile_id])
        source[playerShot.player.profile_id] = {
          accountId: playerShot.player.account_id,
          profileId: playerShot.player.profile_id,
        };
      if (source[playerShot.player.profile_id])
        source[playerShot.player.profile_id].nickname = betterNickname(
          playerShot.player.nickname,
          fallbackNickname,
        );
    }
    if (
      !filters ||
      (filters?.groupId &&
        filters.groupId === playerShot.damage_info.player_info.player.group_id)
    ) {
      const accId = playerShot.damage_info.player_info.player.account_id;
      const charId = playerShot.damage_info.player_info.player.profile_id;
      if (init && !source[charId])
        source[charId] = {
          accountId: accId,
          profileId: charId,
        };
      if (source[charId])
        source[charId].nickname = betterNickname(
          playerShot.damage_info.player_info.nickname,
          fallbackNickname,
        );
    }
  }
}

function addItemAddedRemoved(
  source: object,
  liveGame: DeepReadonly<LiveGame> | LiveGame,
  fallbackNickname: string,
  init = false,
): void {
  const target = liveGame?.itemsAddedRemoved;
  if (!Array.isArray(target)) return;
  for (const itemAddedRemoved of target) {
    const charId = itemAddedRemoved.player.profile_id;
    if (init && !source[charId]) {
      source[charId] = {
        accountId: itemAddedRemoved.player.profile.account_id,
        profileId: charId,
      };
    }
    if (source[charId])
      source[charId].nickname = betterNickname(
        itemAddedRemoved.player.profile.nickname,
        fallbackNickname,
      );
  }
}

export type GroupedProfiles = Array<{
  accountId: string;
  groupIds: {
    [characterId: string]: string;
  };
  characterId: string;
  characterIds: {
    [characterId: string]: FactionRE;
  };
  probabilityCheck: {
    [characterId: string]: number;
  };
}>;

export function getGroupedProfiles(
  liveGame: DeepReadonly<LiveGame> | LiveGame,
): GroupedProfiles {
  const players: GroupedProfiles = [];
  if (!liveGame?.registerPlayer) return players;
  for (const _profileId in liveGame.registerPlayer) {
    const profile = liveGame.registerPlayer[_profileId];
    const {
      account_id: accountId,
      profile_id: profileId,
      group_id: groupId,
    } = profile.player;
    const isBot = isBotAccount(accountId);
    const prev = players.find((i) => i.accountId === accountId);
    if (prev && !isBot) {
      prev.characterIds[profileId] = profile.player.side;
      prev.probabilityCheck[profileId] = 0;
    } else {
      players.push({
        accountId,
        groupIds: {
          [profileId]: groupId,
        },
        characterId: "", // The result of the probability check
        characterIds: {
          [profileId]: profile.player.side,
        },
        probabilityCheck: {
          [profileId]: 0,
        },
      });
    }
  }
  // This is a very expensive computation that can be on average of 0.5mb - 5mb of occupied memory for the user
  // but due players having an x amount of profiles being registered within a single match, we need to find the
  // most probable profile in use while the player was playing a match
  let searchString = "";
  for (const player of players) {
    const characterIds = Object.keys(player.characterIds);
    // If at minimum, a player contains more than 1 character id, we must stringify the entire game session
    if (characterIds.length < 2) continue;
    searchString = JSON.stringify(liveGame);
    break;
  }
  // Assign each player a primary character id
  for (const player of players) {
    const characterIds = Object.keys(player.characterIds);
    const length = characterIds.length;
    if (!length) continue; // Not possible, but for safety
    if (length === 1) {
      player.characterId = characterIds[0];
    } else if (length >= 2) {
      // Populate the probability check
      for (const characterId in player.probabilityCheck) {
        player.probabilityCheck[characterId] =
          searchString.match(new RegExp(characterId, "g"))?.length ?? 0;
      }
      // Select the most probable profile
      const odds = Object.entries(player.probabilityCheck).sort(
        ([, a], [, b]) => b - a,
      );
      if (!odds.length) continue; // Not possible, but for safety
      // Conditions for the correct character id
      // 1. If there is a probability table but all the values are 0, we default to the SCAV profile
      // 2. If there are values in the table, we select the profile that scored the highest
      const characterId =
        odds[0][1] === 0
          ? Object.entries(characterIds).find(
              ([, faction]) => faction === FactionRE.savage,
            )[0][0]
          : odds[0][0];
      player.characterId = characterId;
    }
  }
  searchString = "";
  return players;
}
